import { CreateSendMethodEnum, TCreateSendMethod, TCreateSendStep } from '../types/createSendFlow';

export type TCtaIcon = 'done' | 'link';

export interface IFlowStepCopy {
  title: string;
  finalCta?: { label: string; icon: TCtaIcon };
}

export const CREATE_SEND_FLOW_COPY: Record<TCreateSendMethod, Partial<Record<TCreateSendStep, IFlowStepCopy>>> = {
  [CreateSendMethodEnum.Recipients]: {
    contents: { title: 'Send to recipients' },
    recipients: { title: 'Recipients' },
    'claim-page': { title: 'Claim page' },
    email: { title: 'Compose email' },
    'custom-message': { title: 'Custom message' },
    review: { title: 'Review & send', finalCta: { label: 'Confirm and send', icon: 'done' } },
  },
  [CreateSendMethodEnum.Link]: {
    contents: { title: 'Share via link' },
    'link-settings': { title: 'Link settings' },
    'claim-page': { title: 'Claim page' },
    email: { title: 'Compose email' },
    'custom-message': { title: 'Custom message' },
    review: { title: 'Review & send', finalCta: { label: 'Generate link', icon: 'link' } },
  },
  [CreateSendMethodEnum.Location]: {
    contents: { title: 'Ship to location' },
    'shipment-info': { title: 'Shipment info' },
    review: { title: 'Review & send', finalCta: { label: 'Confirm and send', icon: 'done' } },
  },
};

export const resolveFlowStepCopy = (
  method: TCreateSendMethod | null,
  stepId: string | undefined,
): IFlowStepCopy | undefined =>
  method && stepId ? CREATE_SEND_FLOW_COPY[method]?.[stepId as TCreateSendStep] : undefined;

export const CREATE_SEND_COPY = {
  sendMethod: {
    title: 'Send method',
    recipients: {
      title: 'Send to recipients',
      description: 'Send a gift directly to one or more recipients via email',
    },
    link: {
      title: 'Share via link',
      description: 'Generate a shareable link recipients can use to claim their gift',
    },
    location: {
      title: 'Ship to location',
      description: 'Ship items directly to a physical address without recipient notification',
    },
  },
};

export const CONTENT_TYPE_LABEL = {
  pickYourGift: 'Pick your gift',
  customItems: 'Custom items',
} as const;

export const SEND_METHOD_LABEL: Record<TCreateSendMethod, string> = {
  recipients: CREATE_SEND_COPY.sendMethod.recipients.title,
  link: CREATE_SEND_COPY.sendMethod.link.title,
  location: CREATE_SEND_COPY.sendMethod.location.title,
};

export const SEND_METHOD_SWITCH_COPY = {
  title: (target: TCreateSendMethod) => `Switch to ${SEND_METHOD_LABEL[target]}?`,
  message: (current: TCreateSendMethod) =>
    `Everything you've set up for ${SEND_METHOD_LABEL[current]} will be cleared, and you'll start this send from the beginning`,
  confirmButtonText: 'Switch',
  cancelButtonText: 'Cancel',
};

export const resolveCreateSendTitle = (stepId: string | undefined, method: TCreateSendMethod | null): string => {
  if (!stepId) {
    return CREATE_SEND_COPY.sendMethod.title;
  }
  return resolveFlowStepCopy(method, stepId)?.title ?? '';
};

export const stepProgress = (currentIndex: number, stepCount: number): number => {
  if (currentIndex < 0 || stepCount <= 0) {
    return 0;
  }
  return Math.round(((currentIndex + 2) / (stepCount + 1)) * 100);
};

export const CREATE_SEND_MIN_VIEWPORT_WIDTH = 768;
